import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtNum } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Cow, ChartLineUp, Warning, Scales, Trophy, Tree, ArrowDown, ArrowUp } from "@phosphor-icons/react";
import { PageLoading } from "@/components/layout/Layout";
import { useT } from "@/contexts/I18nContext";
import { SystemBadge } from "@/components/Badges";

function Kpi({ label, value, icon: Icon, hint, color = "#083D1E", testid }) {
    return (
        <Card className="p-6 card-paper shadow-none hover:-translate-y-1 hover:shadow-md transition-all duration-200">
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">{label}</div>
                    <div className="metric-number text-3xl mt-2" style={{ color }} data-testid={testid}>
                        {value}
                    </div>
                    {hint ? <div className="text-xs text-muted-foreground mt-1.5">{hint}</div> : null}
                </div>
                <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: "#EAF3DA" }}>
                    <Icon size={20} weight="regular" color={color} />
                </div>
            </div>
        </Card>
    );
}

export default function Dashboard() {
    const { t } = useT();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/dashboard/summary")
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <PageLoading />;
    if (!data) return <div className="text-muted-foreground">—</div>;

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            <header>
                <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">{t("dashboard_title")}</h1>
                <p className="text-sm text-muted-foreground mt-1">{t("dashboard_subtitle")}</p>
            </header>

            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Kpi label={t("active_animals")} value={data.total_animals} icon={Cow} testid="kpi-animals" />
                <Kpi label={t("total_reports")} value={data.total_reports} icon={ChartLineUp} testid="kpi-reports" />
                <Kpi
                    label={t("avg_fcr_as_fed")}
                    value={fmtNum(data.avg_fcr_as_fed)}
                    icon={Scales}
                    hint={t("over_n_reports", { n: data.total_reports })}
                    testid="kpi-avg-fcr-as-fed"
                />
                <Kpi
                    label={t("avg_fcr_dry_matter")}
                    value={fmtNum(data.avg_fcr_dry_matter)}
                    icon={Scales}
                    color="#4A6E00"
                    hint={t("over_n_reports", { n: data.total_reports })}
                    testid="kpi-avg-fcr-dm"
                />
            </section>

            {data.alerts?.length > 0 && (
                <section>
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <Warning size={20} color="#C55D42" weight="fill" />
                        <h2 className="font-display text-xl font-semibold">{t("low_efficiency_alerts")}</h2>
                        <Badge className="bg-[#C55D42] text-white hover:bg-[#C55D42]" data-testid="alerts-count">
                            {data.alerts.length}
                        </Badge>
                    </div>
                    <Card className="card-paper shadow-none overflow-hidden" data-testid="alerts-table">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-[#F9F6ED]">
                                    <TableHead>{t("rfid")}</TableHead>
                                    <TableHead>{t("name")}</TableHead>
                                    <TableHead>{t("production_system")}</TableHead>
                                    <TableHead className="text-right">{t("fcr_as_fed")}</TableHead>
                                    <TableHead></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.alerts.map((a) => (
                                    <TableRow key={a.animal_id} className="hover:bg-[#EAF3DA]/50">
                                        <TableCell className="font-medium">{a.rfid}</TableCell>
                                        <TableCell>{a.name}<div className="text-xs text-muted-foreground">{a.lot}</div></TableCell>
                                        <TableCell><SystemBadge system={a.production_system || "feedlot"} /></TableCell>
                                        <TableCell className="text-right metric-number text-[#C55D42]">{fmtNum(a.fcr_as_fed)}</TableCell>
                                        <TableCell className="text-right">
                                            <Link to={`/animales/${a.animal_id}`} className="text-sm text-[#083D1E] font-medium hover:underline">
                                                {t("view")} →
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Card>
                </section>
            )}

            {/* Per-system averages with top efficient / deficient */}
            <section>
                <div className="flex items-center gap-2 mb-3">
                    <Tree size={20} color="#083D1E" weight="regular" />
                    <h2 className="font-display text-xl font-semibold">{t("system_averages")}</h2>
                </div>
                <p className="text-xs text-muted-foreground mb-4">{t("system_averages_desc")}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {(data.system_averages || []).map((s) => (
                        <Card key={s.system} className="p-5 card-paper shadow-none flex flex-col" data-testid={`system-card-${s.system}`}>
                            <div className="flex items-center justify-between mb-3">
                                <SystemBadge system={s.system} />
                                <div className="text-xs text-muted-foreground">
                                    {s.animals_count} {t("animals").toLowerCase()}
                                </div>
                            </div>
                            <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">{t("avg_fcr_as_fed")}</div>
                            <div className="metric-number text-3xl text-[#083D1E] mt-1" data-testid={`system-avg-${s.system}`}>
                                {fmtNum(s.avg_fcr_as_fed)}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">{s.reports_count} {t("reports").toLowerCase()}</div>

                            {/* Most efficient */}
                            <div className="mt-5 pt-4 border-t border-[#DDE6D3]">
                                <div className="flex items-center gap-1.5 mb-2">
                                    <ArrowDown size={14} color="#083D1E" weight="bold" />
                                    <div className="text-[11px] uppercase tracking-wide text-[#083D1E] font-semibold">{t("most_efficient")}</div>
                                </div>
                                {s.top_efficient.length === 0 ? (
                                    <div className="text-xs text-muted-foreground italic">{t("no_data")}</div>
                                ) : (
                                    <ul className="space-y-1" data-testid={`top-efficient-${s.system}`}>
                                        {s.top_efficient.map((a) => (
                                            <li key={a.animal_id + a.period_end} className="flex items-center justify-between text-sm">
                                                <Link to={`/animales/${a.animal_id}`} className="font-medium text-[#083D1E] hover:underline truncate mr-2">
                                                    {a.name}
                                                </Link>
                                                <span className="metric-number text-sm text-[#083D1E] shrink-0">{fmtNum(a.fcr_as_fed)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* Most deficient */}
                            <div className="mt-4 pt-4 border-t border-[#DDE6D3]">
                                <div className="flex items-center gap-1.5 mb-2">
                                    <ArrowUp size={14} color="#C55D42" weight="bold" />
                                    <div className="text-[11px] uppercase tracking-wide text-[#C55D42] font-semibold">{t("most_deficient")}</div>
                                </div>
                                {s.top_deficient.length === 0 ? (
                                    <div className="text-xs text-muted-foreground italic">{t("no_data")}</div>
                                ) : (
                                    <ul className="space-y-1" data-testid={`top-deficient-${s.system}`}>
                                        {s.top_deficient.map((a) => (
                                            <li key={a.animal_id + a.period_end} className="flex items-center justify-between text-sm">
                                                <Link to={`/animales/${a.animal_id}`} className="font-medium text-[#083D1E] hover:underline truncate mr-2">
                                                    {a.name}
                                                </Link>
                                                <span className="metric-number text-sm text-[#C55D42] shrink-0">{fmtNum(a.fcr_as_fed)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </Card>
                    ))}
                </div>
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="card-paper shadow-none overflow-hidden" data-testid="lot-averages-card">
                    <div className="p-6 border-b border-[#DDE6D3]">
                        <h2 className="font-display text-xl font-semibold">{t("lot_averages")}</h2>
                        <p className="text-xs text-muted-foreground mt-1">{t("lot_averages_desc")}</p>
                    </div>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t("lot")}</TableHead>
                                <TableHead className="text-right">{t("animals")}</TableHead>
                                <TableHead className="text-right">{t("reports")}</TableHead>
                                <TableHead className="text-right">{t("fcr_as_fed")}</TableHead>
                                <TableHead className="text-right">{t("fcr_dry_matter")}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {data.lot_averages.length === 0 ? (
                                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">{t("no_data")}</TableCell></TableRow>
                            ) : data.lot_averages.map((l) => (
                                <TableRow key={l.lot} className="hover:bg-[#EAF3DA]/50">
                                    <TableCell><Badge variant="secondary">{l.lot}</Badge></TableCell>
                                    <TableCell className="text-right">{l.animals_count}</TableCell>
                                    <TableCell className="text-right">{l.reports_count}</TableCell>
                                    <TableCell className="text-right metric-number">{fmtNum(l.avg_fcr_as_fed)}</TableCell>
                                    <TableCell className="text-right metric-number">{fmtNum(l.avg_fcr_dry_matter)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>

                <Card className="card-paper shadow-none overflow-hidden" data-testid="ranking-card">
                    <div className="p-6 border-b border-[#DDE6D3]">
                        <div className="flex items-center gap-2">
                            <Trophy size={18} color="#4A6E00" weight="fill" />
                            <h2 className="font-display text-xl font-semibold">{t("ranking_title")}</h2>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{t("ranking_desc")}</p>
                    </div>
                    <div className="max-h-[420px] overflow-auto">
                        <Table>
                            <TableHeader className="sticky top-0 bg-white">
                                <TableRow>
                                    <TableHead className="w-12">#</TableHead>
                                    <TableHead>{t("animal")}</TableHead>
                                    <TableHead>{t("lot")}</TableHead>
                                    <TableHead className="text-right">{t("best_fcr")}</TableHead>
                                    <TableHead className="text-right">{t("last")}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.ranking.length === 0 ? (
                                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">{t("no_data")}</TableCell></TableRow>
                                ) : data.ranking.slice(0, 20).map((r, idx) => (
                                    <TableRow key={r.animal_id} className="hover:bg-[#EAF3DA]/50">
                                        <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                                        <TableCell>
                                            <Link to={`/animales/${r.animal_id}`} className="font-medium hover:underline">{r.name}</Link>
                                            <div className="text-xs text-muted-foreground">{r.rfid}</div>
                                        </TableCell>
                                        <TableCell><Badge variant="secondary">{r.lot}</Badge></TableCell>
                                        <TableCell className="text-right metric-number text-[#083D1E]">{fmtNum(r.best_fcr_as_fed)}</TableCell>
                                        <TableCell className="text-right metric-number text-muted-foreground">{fmtNum(r.last_fcr_as_fed)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Card>
            </section>
        </div>
    );
}
